/** @module Auth0MfaCodePageHelper */
/* eslint-disable import/extensions */
import { Auth0PageBase } from './auth0-page-template.mst.mjs';
import CodeEntryHtml from '../../../public/mfaOobCodePage.html.mst';
import Auth0UtilsBrowser from '../../utils/auth0-utils-browser.mjs';
import Auth0Localizer from '../../utils/auth0-localization-helper.mjs';
import Auth0Cdn from '../utils/auth0-cdn-resource-resolver.mst.mjs';
import Auth0Constants from '../../utils/auth0-constants.mjs';
import Auth0WhiteLabelAssetHelper from '../utils/auth0-white-label-helper.mst.mjs';
import AuthAnalytics from '../../utils/auth-analytics.mjs';
import {
  STEP_MFA_CODE_ENTRY_PAGE,
  EVENT_MFA_FACTOR_SELECTED,
  EVENT_MFA_CODE_ENTERED,
  EVENT_MFA_CODE_SUBMITTED,
  EVENT_MFA_CODE_RESEND_REQUESTED,
  EVENT_MFA_PASSWORD_SUBMITTED,
  EVENT_MFA_VERIFICATION_SUCCESS,
  EVENT_MFA_VERIFICATION_FAILED,
  EVENT_MFA_CANCEL,
} from '../../utils/auth-analytics-constants.mjs';

const PAGE_MODE_MFA_CODE = 'mfa-code';
const PAGE_MODE_MFA_PASSWORD = 'mfa-token';

class Auth0MfaCodePageHelper extends Auth0PageBase {
  constructor(options) {
    super(options);
    console.log(`Auth0MfaCodePageHelper initialized => ${JSON.stringify(options)}`);
    this._mfaFactorType = options.mfaFactorType;
    this._mfaGrantToken = options.mfaGrantToken;
    this._mfaUriState = options.mfaState;

    // this page is invoked by the Auth0 AdaptiveAMFAController and, as such, will need to get
    // its white label options from the MFA Grant Token
    this._whiteLabelOptions = this._grantTokenWhiteLabelOptions;
    this._whiteLabelHelper = new Auth0WhiteLabelAssetHelper({
      tenantOrDomain: options.tenantOrDomain,
      ...this._whiteLabelOptions,
    });

    // start controlled options
    // these allow for overriding default business logic with custom logic
    this.controlledOnContinue = options.onContinue;
    this.controlledOnVerify = options.onVerify;
    this.controlledSendVerificationCode = options.sendVerificationCode;
    this._controlledOnOptOut = options.onOptOut;
    this._userPhone = options.userPhone;

    // allow for copy overrides based on how this page is being used
    this.titleResourceId = options.titleResourceId || 'mfa-enter-verification-code-title';
    this.preambleResourceId = options.preambleResourceId;

    this._currentChallenge = options.currentChallenge || null;
    this._onGoBack = options.onGoBack;
    this._sentToValue = options.sentToValue;
    this._shouldHideSendCode = options.shouldHideSendCode;
    // end controlled options

    // always start in CODE mode. A failure here may put us into password mode
    this._pageMode = PAGE_MODE_MFA_CODE;

    this._pageStyles = [
      `${Auth0Cdn.AUTH0_STYLES_URL}/auth0-mfa-styles.css?cb=71c5d67`,
    ];

    // Errors that result in transition to password mode
    this.PASSWORD_MODE_ERRORS = [
      Auth0Constants.MFA_ERROR_CODE_INIT_MFA_TOKEN_FETCH_FAIL,
      Auth0Constants.MFA_ERROR_CODE_INIT_MFA_TOKEN_FETCH_FAIL_USER_MISSING,
      Auth0Constants.MFA_ERROR_CODE_INIT_INTERMEDIATE_JWT_INVALID,
    ];
  }

  get _grantTokenContinueUrl() {
    return Auth0UtilsBrowser.getJwtBodyFromToken(this._mfaGrantToken)?.continueUrl;
  }

  get _grantTokenUserId() {
    return Auth0UtilsBrowser.getJwtBodyFromToken(this._mfaGrantToken)?.userId;
  }

  get _grantTokenUserEmail() {
    return Auth0UtilsBrowser.getJwtBodyFromToken(this._mfaGrantToken)?.email;
  }

  get _grantTokenAmfaFactorId() {
    return Auth0UtilsBrowser.getJwtBodyFromToken(this._mfaGrantToken)?.amfaFactorId;
  }

  get _grantTokenAmfaFactorEmailAddress() {
    return Auth0UtilsBrowser.getJwtBodyFromToken(this._mfaGrantToken)?.amfaFactorEmailAddress;
  }

  get _grantTokenClientId() {
    return Auth0UtilsBrowser.getJwtBodyFromToken(this._mfaGrantToken)?.clientId;
  }

  get _grantTokenState() {
    return Auth0UtilsBrowser.getJwtBodyFromToken(this._mfaGrantToken)?.state;
  }

  get _grantTokenWhiteLabelOptions() {
    const json = Auth0UtilsBrowser.getJwtBodyFromToken(this._mfaGrantToken)?.whiteLabelOptionsJson;
    return json ? JSON.parse(json) : {};
  }

  get _maskedEmailAddressDisplayString() {
    if (this._testIsEmailAddress(this._grantTokenAmfaFactorEmailAddress)) {
      return Auth0UtilsBrowser.getMaskedEmailAddressDisplayString(this._grantTokenAmfaFactorEmailAddress);
    }
    if (this._testIsEmailAddress(this._grantTokenUserEmail)) {
      return Auth0UtilsBrowser.getMaskedEmailAddressDisplayString(this._grantTokenUserEmail);
    }
    return 'your email address';
  }

  get _grantTokenUsername() {
    return Auth0UtilsBrowser.getJwtBodyFromToken(this._mfaGrantToken)?.username;
  }

  get _grantTokenAdaptiveMfaTriggerIds() {
    return Auth0UtilsBrowser.getJwtBodyFromToken(this._mfaGrantToken)?.triggerIds;
  }

  get _grantTokenAdaptiveMfaSessionId() {
    return Auth0UtilsBrowser.getJwtBodyFromToken(this._mfaGrantToken)?.sessionId;
  }

  get _resendCodeLink() {
    return document.getElementById('mfa-resend-code-link');
  }

  get _mfaButtonText() {
    return this._mfaButton.innerText;
  }

  set _mfaButtonText(value) {
    this._mfaButton.innerText = value;
  }

  get _mfaButton() {
    return document.getElementById('mfa-button');
  }

  get _mfaButtonContainer() {
    return document.getElementById('mfa-button-container');
  }

  get _mfaPasswordContainer() {
    return document.getElementById('mfa-password-container');
  }

  get _mfaPasswordInput() {
    return document.getElementById('mfa-password-input');
  }

  _getMfaPasswordInputValue() {
    if (!this._mfaPasswordInput) {
      return '';
    }
    return this._mfaPasswordInput.value;
  }

  get _mfaCodeInputContainer() {
    return document.getElementById('mfa-code-container');
  }

  get _mfaCodeInputs() {
    return document.querySelectorAll('.digit-box');
  }

  get _mfaCodeInput() {
    return document.getElementById('mfa-code');
  }

  get _mfaCodeInputValue() {
    return this._mfaCodeInput.value.trim();
  }

  get _isMfaCodeFormatValid() {
    console.log(`_isMfaCodeFormatValid => ${this._mfaCodeInputValue}`);
    // maybe others here... for now, literally just 6 digits... no more no less
    return /^\d{6}$/.test(this._mfaCodeInputValue);
  }

  get _mfaCodeInvalidContainer() {
    return document.getElementById('mfa-invalid-container-inner');
  }

  get _mfaCodeInvalidIcon() {
    return document.getElementById('mfa-invalid-icon');
  }

  get _mfaFormContainer() {
    return document.getElementById('mfa-form-container');
  }

  get _mfaExpiryAndResendContainer() {
    return document.getElementById('mfa-expiry-and-resend');
  }

  get _mfaExpiryContents() {
    return {
      expiry1: document.getElementById('expiry-info-1'),
      expiry2: document.getElementById('expiry-info-2'),
      resendLink: document.getElementById('mfa-resend-code-link'),
    };
  }

  get _mfaSentToEndpointContainer() {
    return document.getElementById('user-id-card');
  }

  get _mfaSentToEndpointTextEle() {
    return document.getElementById('id-card-location');
  }

  get _mfaPasteLink() {
    return document.getElementById('mfa-code-paste');
  }

  get _mfaClearLink() {
    return document.getElementById('mfa-code-clear');
  }

  get _mfaPasteIcon() {
    return document.getElementById('mfa-code-paste-icon');
  }

  get _mfaClearIcon() {
    return document.getElementById('mfa-code-clear-icon');
  }

  get _mfaSentToEndpointText() {
    return this._mfaSentToEndpointTextEle.innerText;
  }

  set _mfaSentToEndpointText(value) {
    console.log(`_mfaSentToEndpointText set to => ${value}`);
    if (this._mfaSentToEndpointTextEle) {
      this._mfaSentToEndpointTextEle.innerText = value || '-';
    }
  }

  get _mfaOptOutLink() {
    return document.getElementById('mfa-opt-out-link');
  }

  get _mfaOptOutContainer() {
    return document.getElementById('mfa-opt-out-container');
  }

  get _mfaChooseAnotherMethodContainer() {
    return document.getElementById('mfa-choose-another-method-wrapper');
  }

  get _mfaChooseAnotherMethodLink() {
    return document.getElementById('mfa-choose-another-link');
  }

  get _mfaIsOobType() {
    return Auth0Constants.MFA_OOB_TYPES.includes(this._mfaFactorType);
  }

  get _mfaIsOtpType() {
    return this._mfaFactorType === Auth0Constants.MFA_TYPE_TOTP;
  }

  get _mfaIsRecoveryCodeType() {
    return this._mfaFactorType === Auth0Constants.MFA_TYPE_RECOVERY_CODE;
  }

  get _mfaShouldShowSentToLocationUi() {
    return this._mfaIsOobType;
  }

  get _mfaPreambleResources() {
    if (this._mfaFactorType === Auth0Constants.MFA_OOB_CHANNEL_EMAIL) {
      return {
        resourceId: 'mfa-preamble-email',
        // replacements: {  }, // can also return replacement vars if needed
      };
    }

    if (this._mfaFactorType === Auth0Constants.MFA_OOB_CHANNEL_SMS) {
      return {
        resourceId: 'mfa-additional-enrollment-sms-preamble',
      };
    }

    if (this._mfaFactorType === Auth0Constants.MFA_OOB_CHANNEL_VOICE) {
      return {
        resourceId: 'mfa-voice-challenge-preamble',
      };
    }

    if (this._mfaFactorType === Auth0Constants.MFA_TYPE_TOTP) {
      return {
        resourceId: 'mfa-totp-challenge-preamble',
      };
    }

    // add other factors here

    throw new Error('Factor type is not supported in _mfaPreambleResources');
  }

  get _mfaSentCodeToLocation() {
    if (this._sentToValue) {
      return this._sentToValue;
    }
    if (this._mfaFactorType === Auth0Constants.MFA_OOB_CHANNEL_EMAIL) {
      return this._grantTokenUserEmail;
    }

    if (this._mfaFactorType === Auth0Constants.MFA_OOB_CHANNEL_SMS || this._mfaFactorType === Auth0Constants.MFA_OOB_CHANNEL_VOICE) {
      return this._userPhone;
    }

    // add other factors here, e.g. sms and voice would return the phone number, etc

    return '';
  }

  get _mfaResendCodeResources() {
    if (this._mfaFactorType === Auth0Constants.MFA_OOB_CHANNEL_SMS) {
      return 'mfa-resend-sms-code';
    }

    if (this._mfaFactorType === Auth0Constants.MFA_OOB_CHANNEL_VOICE) {
      return 'mfa-resend-voice-code';
    }

    if (this._mfaFactorType === Auth0Constants.MFA_OOB_CHANNEL_EMAIL) {
      return 'mfa-resend-email-code';
    }

    return 'mfa-resend-code-link';
  }

  get _mfaCodeLength() {
    const codeLength = {
      [Auth0Constants.MFA_OOB_CHANNEL_EMAIL]: 6,
      [Auth0Constants.MFA_OOB_CHANNEL_SMS]: 6,
      [Auth0Constants.MFA_TYPE_TOTP]: 6,
      // add other factors here
    };
    if (codeLength[this._mfaFactorType]) {
      return codeLength[this._mfaFactorType];
    }

    throw new Error('Factor type is not supported in _mfaCodeLength');
  }

  get _isInCodeMode() { return this._pageMode === PAGE_MODE_MFA_CODE; }

  get _isInPasswordMode() { return this._pageMode === PAGE_MODE_MFA_PASSWORD; }

  _testIsEmailAddress = (value) => {
    if (!value) return false;
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
  };

  _buildMfaSegmentContext = () => {
    return AuthAnalytics.buildSigninContext({
      step: STEP_MFA_CODE_ENTRY_PAGE,
      client_id: this._grantTokenClientId,
      tenant: this.config?.auth0Tenant,
    });
  };

  _trackMfaEvent = (segmentEventId, additionalProperties = {}) => {
    AuthAnalytics.track(segmentEventId, {
      email: this._grantTokenUserEmail,
      user_id: this._grantTokenUserId,
      factor_id: this._grantTokenAmfaFactorId,
      factor_type: this._mfaFactorType,
      ...additionalProperties,
    }, this._buildMfaSegmentContext());
  };

  _registerMfaCodeModeAnalytics = () => {
    // Verification code entry tracking (also checks on button click to handle autofill + immediate click)
    AuthAnalytics.registerTextEntryTracking(this._mfaCodeInput, () => {
      this._trackMfaEvent(EVENT_MFA_CODE_ENTERED);
    }, { alsoCheckOn: this._mfaButton });

    // Verify button click tracking (EVENT_MFA_CODE_SUBMITTED)
    // Uses inline tracking in _verifyCodeHandler() to handle both button clicks and auto-submit

    // Resend code link click
    AuthAnalytics.registerButtonClickEvent(this._resendCodeLink, () => {
      this._trackMfaEvent(EVENT_MFA_CODE_RESEND_REQUESTED);
    });

    // Cancel/logout link tracking (EVENT_MFA_CANCEL)
    // Uses inline await pattern before navigation in _cancelMfaAndLogout() handler
  };

  _registerMfaPasswordModeAnalytics = () => {
    // Password entry tracking (also checks on button click to handle autofill + immediate click)
    AuthAnalytics.registerTextEntryTracking(this._mfaPasswordInput, () => {
      this._trackMfaEvent(EVENT_MFA_CODE_ENTERED);
    }, { alsoCheckOn: this._mfaButton });

    // Submit button click - tracks when password is submitted to get MFA token
    AuthAnalytics.registerButtonClickEvent(this._mfaButton, () => {
      this._trackMfaEvent(EVENT_MFA_PASSWORD_SUBMITTED);
    });

    // Cancel/logout link tracking (EVENT_MFA_CANCEL)
    // Uses inline await pattern before navigation in _cancelMfaAndLogout() handler
  };

  _updateActiveBox = () => {
    const cursorPos = this._mfaCodeInput.selectionStart || 0;
    this._mfaCodeInputs.forEach((box, i) => {
      box.classList.toggle('active', i === cursorPos);
    });
  };

  _updateBoxes = () => {
    const val = this._mfaCodeInputValue.replace(/\D/g, '').slice(0, 6);
    this._mfaCodeInputs.forEach((box, i) => {
      box.textContent = val[i] || '';
    });
    this._updateActiveBox();
  };

  _getMaskedContentFromChallengeContext = (contextToken) => {
    console.log(`_getMaskedContentFromChallengeContext context token => ${contextToken}`);
    if (this._sentToValue) {
      return this._sentToValue;
    }
    if (this._mfaFactorType === Auth0Constants.MFA_TYPE_TOTP) {
      return '';
    }
    if (this._mfaFactorType === Auth0Constants.MFA_OOB_CHANNEL_SMS) {
      const challengePhoneUsed = Auth0UtilsBrowser.getJwtBodyFromToken(contextToken)?.amfaFactorPhone;
      if (!challengePhoneUsed) {
        return this._userPhone;
      }
      return challengePhoneUsed;
    }
    // this will extract the email address from the challenge context and mask it
    // the email address may or may not be already masked
    if (!contextToken) return this._maskedEmailAddressDisplayString;
    const challengeAddressUsed = Auth0UtilsBrowser.getJwtBodyFromToken(contextToken)?.amfaFactorEmailAddress;
    console.log(`_getMaskedContentFromChallengeContext challengeAddressUsed => ${challengeAddressUsed}`);
    if (!challengeAddressUsed) return this._maskedEmailAddressDisplayString;
    const masked = Auth0UtilsBrowser.getMaskedEmailAddressDisplayString(challengeAddressUsed);
    console.log(`_getMaskedContentFromChallengeContext masked => ${masked}`);
    return masked;
  };

  _sendVerificationCode = async (isInitialLoad) => {
    try {
      if (this._currentChallenge && isInitialLoad) {
        return;
      }

      const challengeOptions = {
        state: this._mfaUriState,
        mfaGrantToken: this._mfaGrantToken,
        amfaFactorId: this._grantTokenAmfaFactorId,
      };
      if (this._getMfaPasswordInputValue()) {
        challengeOptions.password = this._getMfaPasswordInputValue();
      }
      console.log(`_sendVerificationCode challengeOptions => ${JSON.stringify(challengeOptions)}`);
      if (this.controlledSendVerificationCode) {
        this._currentChallenge = await this.controlledSendVerificationCode(challengeOptions);
      } else {
        this._currentChallenge = await this._auth0ManagementApi.autoEnrollOrChallengeAmfaFactor(challengeOptions);
      }

      console.log(`_sendVerificationCode challenge set to => ${JSON.stringify(this._currentChallenge)}`);
      // always update the sent to endpoint afterward since, even on a challenge the factor can change
      // this wont always be an email now...what do?
      this._mfaSentToEndpointText = this._getMaskedContentFromChallengeContext(this._currentChallenge.amfaContext);
    } catch (error) {
      this._currentChallenge = null;
      console.log(`_sendVerificationCode error => ${error.message} (code: ${error.code})`);
      // error may be that could not get an MFA token because IdS user not found in cache
      // if true, go into password mode so we can renegotiate the token
      if (this.PASSWORD_MODE_ERRORS.includes(error.code)) {
        throw error;
      }

      // otherwise show error
      this._showCodedMfaInitError(error.code);
    }
  };

  _resendVerificationCode = async () => {
    try {
      this._disableButton(this._mfaButton, 'mfa-resend-please-wait');
      await this._sendVerificationCode();
      this._clearMfaInputCode();
      this._enableButton(this._mfaButton, 'mfa-enter-code-button');
    } catch (error) {
      console.log(`_resendVerificationCode error => ${error.message} (code: ${error.code})`);
      if (this.PASSWORD_MODE_ERRORS.includes(error.code)) {
        // expected error... need to collect user password first (will happen if resend request > 10 min from render)
        await this._startMfaTokenPasswordMode(true);
      } else {
        // otherwise show error
        this._currentChallenge = null;
        this._showCodedMfaInitError(error.code);
      }
    }
  };

  _buttonClickHandler = async () => {
    if (this._isInCodeMode) {
      await this._verifyCodeHandler();
    } else if (this._isInPasswordMode) {
      await this._startMfaFlowWithPasswordHandler();
    }
  };

  _startMfaFlowWithPasswordHandler = async () => {
    console.log('_startMfaFlowWithPasswordHandler start...');

    // restart same exact flow to send the code out, but with a user password this time
    try {
      this._disableButton(this._mfaButton, 'mfa-please-wait-short');
      await this._sendVerificationCode();
      this._trackMfaEvent(EVENT_MFA_FACTOR_SELECTED);
      // success using password, transition to code entry mode
      await this._startMfaCodeMode(true);
    } catch (error) {
      this._currentChallenge = null;
      this._displayError('mfa-invalid-password');
      this._enableButton(this._mfaButton, 'mfa-provide-password-button');
    }
  };

  _verifyCodeHandler = async () => {
    if (!this._isMfaCodeFormatValid) {
      this._displayError('mfa-code-invalid-text');
      return;
    }

    // Track submit event (handles both button click and auto-submit from paste/typing)
    // Await because successful verification navigates away
    await this._trackMfaEvent(EVENT_MFA_CODE_SUBMITTED);

    console.log('_verifyCode start...');
    this._disableButton(this._mfaButton, 'please-wait-generic');
    const bindingCode = this._mfaCodeInputValue;

    if (this._mfaFactorType !== Auth0Constants.MFA_TYPE_TOTP && !this._currentChallenge.oobCode) {
      this._showCodedMfaVerifyError(Auth0Constants.MFA_ERROR_CODE_VERIFY_WITHOUT_CHALLENGE);
      this._enableButton(this._mfaButton, 'mfa-enter-code-button');
      return;
    }
    console.log('Found OOB code for verify =>', this._currentChallenge.oobCode);
    try {
      let verificationTokenResponse;
      if (this.controlledOnVerify) {
        verificationTokenResponse = await this.controlledOnVerify({
          bindingCode,
          oobCode: this._currentChallenge.oobCode,
          mfaToken: this._currentChallenge.mfaToken,
          mfaGrantToken: this._mfaGrantToken,
          mfaEnrollment: !!this.controlledOnContinue,
          type: this._mfaFactorType,
        });
        console.log('checking controlledOnVerify', verificationTokenResponse);
      } else {
        let payload;
        if (this._mfaFactorType === Auth0Constants.MFA_TYPE_TOTP) {
          payload = {
            otpCode: bindingCode,
            mfaGrantToken: this._mfaGrantToken,
            mfaToken: this._currentChallenge.mfaToken,
            challengeType: Auth0Constants.MFA_TYPE_OTP,
          };
        } else {
          payload = {
            bindingCode,
            oobCode: this._currentChallenge.oobCode,
            mfaToken: this._currentChallenge.mfaToken,
            mfaGrantToken: this._mfaGrantToken,
            userPhone: this._userPhone,
            challengeType: Auth0Constants.MFA_TYPE_OOB,
          };
        }
        verificationTokenResponse = await this._auth0ManagementApi.verifyMfaCode(payload);
      }

      // if we didn't throw, the challenge was validated successfully so continue
      // Track MFA verification success before navigation
      await this._trackMfaEvent(EVENT_MFA_VERIFICATION_SUCCESS);

      if (this.controlledOnContinue) {
        this.controlledOnContinue(verificationTokenResponse);
        return;
      }
      this._continueWithLogin(verificationTokenResponse.accessToken);
    } catch (error) {
      // Track MFA verification failure with error details
      this._trackMfaEvent(EVENT_MFA_VERIFICATION_FAILED, {
        error_message: error?.message || 'Unknown error',
        error_code: error?.code || error?.statusCode,
      });

      this._showCodedMfaVerifyError(Auth0Constants.MFA_ERROR_CODE_VERIFY_FAILED);
      this._enableButton(this._mfaButton, 'mfa-enter-code-button');
    }
  };

  _showCodedMfaInitError = (code) => {
    this._hideInfo();
    this._displayError('error-mfa-initialization', { code, supportEmail: this._whiteLabelHelper.appLoginSupportEmail, supportPhoneNumber: this._whiteLabelHelper.appLoginSupportPhoneNumber });
  };

  _showCodedMfaVerifyError = (code) => {
    this._hideInfo();
    this._displayError('error-mfa-verify', { code, supportEmail: this._whiteLabelHelper.appLoginSupportEmail, supportPhoneNumber: this._whiteLabelHelper.appLoginSupportPhoneNumber });
  };

  _showInvalidEntry = (show, errorTextKey) => {
    Auth0Localizer.setElementLocalizedText({ id: 'mfa-invalid-text', resourceIdOverride: errorTextKey });
    this._showElement(this._mfaCodeInvalidContainer, show);
    if (show) {
      this._mfaCodeInputContainer.classList.add('invalid-input');
    } else {
      this._mfaCodeInputContainer.classList.remove('invalid-input');
    }
  };

  _continueWithLogin = (verifiedMfaToken) => {
    const params = {
      state: this._mfaUriState,
      verifiedMfaToken,
      context: this._currentChallenge.amfaContext,
    };
    const url = `${this._grantTokenContinueUrl}?${new URLSearchParams(params)}`;
    console.log('Auth0MfaCodePageHelper._continueWithLogin to', url);

    document.location = url;
  };

  _clearMfaInputCode = () => {
    this._mfaCodeInput.value = '';
    this._updateBoxes();
    this._showInvalidEntry(false);
    this._mfaCodeInput.focus();
  };

  _getClipboardText = async () => {
    try {
      // Request permission to read clipboard content
      const text = await navigator.clipboard.readText();
      console.log('Clipboard content:', text);
      return text;
    } catch (err) {
      console.error('Failed to read clipboard contents:', err);
    }
  };

  _doCodePaste = async (event) => {
    // The 6-digit code formatting in emails contains spaces for readability. If user copy and pastes it
    // into this field, be kind and strip the spaces out
    event?.preventDefault();
    let pasteData;
    const pasteDataSource = event?.clipboardData || window.clipboardData;
    if (!pasteDataSource) {
      pasteData = await this._getClipboardText();
    } else {
      pasteData = pasteDataSource.getData('text');
    }

    const noSpacesData = pasteData.replace(/\s+/g, '');
    const match = noSpacesData.match(/^.*(\d{6}).*$/);
    if (!match || match.length < 1) {
      this._showInvalidEntry(!this._isMfaCodeFormatValid, 'mfa-code-invalid-paste-text');
      return;
    }

    // eslint-disable-next-line prefer-destructuring
    this._mfaCodeInput.value = match[1];
    this._updateBoxes();
    this._showInvalidEntry(false);
    this._contentContainer.focus();

    await this._verifyCodeHandler();
  };

  _initPreProcessPastedCodeText = () => {
    document.removeEventListener('paste', this._doCodePaste);
    document.addEventListener('paste', this._doCodePaste);
  };

  _initEnterKeyToButtonMapping = () => {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        this._mfaButton.click();
      }
    });
  };

  _isKeyEventNumber = (event) => {
    return /^[0-9]$/.test(event.key);
  };

  _isKeyNotAllowedInput = (event) => {
    if (event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) return false;
    return /^[A-Za-z]$/.test(event.key);
  };

  _mapKeyEventForSwitch = (event) => {
    if (this._isKeyNotAllowedInput(event)) {
      return 'NotAllowed';
    }

    return this._isKeyEventNumber(event)
      ? 'Number'
      : event.key;
  };

  _startMfaCodeMode = async (transition = false) => {
    const alter = () => {
      this._pageMode = PAGE_MODE_MFA_CODE;
      this._hideElement(this._mfaPasswordContainer);
      this._showElement(this._mfaCodeInputContainer, true, 'flex');
      this._hideError();
      if (this._shouldHideSendCode) {
        const { expiry1, expiry2, resendLink } = this._mfaExpiryContents;
        this._hideElement(expiry1);
        this._hideElement(expiry2);
        this._hideElement(resendLink);
      }
      this._mfaButtonContainer.classList.remove('pad-bottom-20');
      Auth0Localizer.setElementLocalizedText({ id: 'mfa-title', resourceIdOverride: this.titleResourceId });
      Auth0Localizer.setElementLocalizedText({ id: 'mfa-button', resourceIdOverride: 'mfa-enter-code-button' });
      Auth0Localizer.setElementLocalizedText({ id: 'mfa-preamble', resourceIdOverride: this._mfaPreambleResources.resourceId }, this._mfaPreambleResources.replacements);
      Auth0Localizer.setElementLocalizedText('mfa-enter-code', { numDigits: this._mfaCodeLength });
      Auth0Localizer.setElementLocalizedText({ id: 'mfa-resend-code-link', resourceIdOverride: this._mfaResendCodeResources });
      Auth0Localizer.setElementLocalizedText('mfa-invalid-text');
      Auth0Localizer.setElementLocalizedText({ id: 'mfa-code-paste-text', resourceIdOverride: 'paste-link-text' });
      Auth0Localizer.setElementLocalizedText({ id: 'mfa-code-clear-text', resourceIdOverride: 'clear-link-text' });
      this._initPreProcessPastedCodeText();

      Auth0Localizer.setElementLocalizedText({ id: 'mfa-opt-out-link', resourceIdOverride: 'mfa-enrollment-decline' });
      Auth0Localizer.setElementLocalizedText({ id: 'expiry-info-1', resourceIdOverride: 'mfa-enrollment-expiry-notice-1' });
      Auth0Localizer.setElementLocalizedText({ id: 'expiry-info-2', resourceIdOverride: 'mfa-enrollment-expiry-notice-2' });
      this._enableButton(this._mfaButton, 'mfa-enter-code-button');

      Auth0Localizer.setElementLocalizedText({ id: 'mfa-footer-1', resourceIdOverride: 'mfa-enrollment-footer-text-1' });
      Auth0Localizer.setElementLocalizedText({ id: 'mfa-footer-2', resourceIdOverride: 'mfa-enrollment-footer-text-2' }, { supportPhoneNumber: this._whiteLabelHelper.appLoginSupportPhoneNumber });

      document.querySelector('.mfa-wrapper').addEventListener('click', () => {
        this._mfaCodeInput.focus();
      });
      this._mfaCodeInput.addEventListener('input', async () => {
        // Remove non-digit characters
        this._mfaCodeInput.value = this._mfaCodeInputValue.replace(/\D/g, '').slice(0, 6);
        this._updateBoxes();
        if (this._isMfaCodeFormatValid && !this._autoSubmitAttempted) {
          this._autoSubmitAttempted = true;
          await this._verifyCodeHandler();
        }
      });
      this._mfaCodeInput.addEventListener('click', this._updateActiveBox);
      this._mfaCodeInput.addEventListener('keyup', this._updateActiveBox);
      this._mfaCodeInput.addEventListener('focus', this._updateActiveBox);
      this._mfaCodeInput.addEventListener('blur', () => {
        setTimeout(() => this._mfaCodeInput.focus(), 0); // Allow event loop to clear first
      });

      this._updateBoxes();
      this._mfaCodeInput.focus();

      // tooltips
      this._mfaPasteLink.title = Auth0Localizer.getLocalizedText('paste-link-tooltip');
      this._mfaClearLink.title = Auth0Localizer.getLocalizedText('clear-link-tooltip');

      // Register all Segment analytics tracking for code mode
      this._registerMfaCodeModeAnalytics();
    };

    if (transition) {
      // structurally, code and password modes are very similar and transitioning to one from the other is subtle (this fade is to show a definite transition into a new mode)
      await this._fadeOutAlterThenFadeBackIn(alter);
    } else {
      alter();
    }
  };

  _startMfaTokenPasswordMode = async (transition = false) => {
    // unable to get an MFA token during code entry mode to start the MFA flow (and get a code), so must
    // collect user password and use it get the MFA token, then start MFA flow
    const alter = () => {
      this._pageMode = PAGE_MODE_MFA_PASSWORD;
      this._showElement(this._mfaPasswordContainer);
      this._hideElement(this._mfaCodeInputContainer);
      this._hideElement(this._mfaExpiryAndResendContainer);
      this._mfaButtonContainer.classList.add('pad-bottom-20');
      Auth0Localizer.setElementLocalizedText({ id: 'mfa-title', resourceIdOverride: 'mfa-enter-password-title' });
      Auth0Localizer.setElementLocalizedText({ id: 'mfa-preamble', resourceIdOverride: 'mfa-preamble-email-password' });
      Auth0Localizer.setElementLocalizedText('mfa-enter-password');
      Auth0Localizer.setElementLocalizedText({ id: 'mfa-footer-1', resourceIdOverride: 'mfa-enrollment-footer-text-1' });
      Auth0Localizer.setElementLocalizedText({ id: 'mfa-footer-2', resourceIdOverride: 'mfa-enrollment-footer-text-2' }, { supportPhoneNumber: this._whiteLabelHelper.appLoginSupportPhoneNumber });
      this._enableButton(this._mfaButton, 'mfa-provide-password-button');
      this._mfaPasswordInput.focus();

      // Register all Segment analytics tracking for password mode
      this._registerMfaPasswordModeAnalytics();
    };

    if (transition) {
      // structurally, code and password modes are very similar and transitioning to one from the other is subtle (this fade is to show a definite transition into a new mode)
      await this._fadeOutAlterThenFadeBackIn(alter);
    } else {
      alter();
    }
  };

  _cancelMfaAndLogout = async () => {
    // Track cancel event before logout/navigation
    await this._trackMfaEvent(EVENT_MFA_CANCEL);
    this._logout();
  };

  _initMfaInputHtml = async () => {
    await this._applyWhiteLabel(this._whiteLabelOptions);

    this._enableButton(this._mfaButton);
    this._initEnterKeyToButtonMapping();
    this._mfaCodeInvalidIcon.src = `${Auth0Cdn.AUTH0_IMAGES_URL}/error-exclamation.svg`;
    this._mfaPasteIcon.src = `${Auth0Cdn.AUTH0_IMAGES_URL}/copy.svg`;
    this._mfaClearIcon.src = `${Auth0Cdn.AUTH0_IMAGES_URL}/circle-x.svg`;
    this._showElement(this._mfaSentToEndpointContainer, this._mfaShouldShowSentToLocationUi);
    this._mfaSentToEndpointText = this._getMaskedContentFromChallengeContext(this._currentChallenge?.amfaContext);

    this._resendCodeLink.onclick = () => { this._resendVerificationCode(); };
    this._mfaButton.onclick = () => { this._buttonClickHandler(); };
    this._mfaPasteLink.onclick = () => { this._doCodePaste(); };
    this._mfaPasteIcon.onclick = () => { this._doCodePaste(); };
    this._mfaClearLink.onclick = () => { this._clearMfaInputCode(); };
    this._mfaClearIcon.onclick = () => { this._clearMfaInputCode(); };

    if (this._controlledOnOptOut) {
      this._showElement(this._mfaOptOutContainer);
      this._mfaOptOutLink.onclick = () => {
        this._controlledOnOptOut([this._mfaButton]);
      };
    }
    if (this._onGoBack) {
      Auth0Localizer.setElementLocalizedText({ id: 'mfa-choose-another-link', resourceIdOverride: 'mfa-choose-another-method' });
      this._showElement(this._mfaChooseAnotherMethodContainer);
      this._mfaChooseAnotherMethodLink.onclick = () => {
        this._onGoBack();
      };
    }
  };

  _localize = async () => {
    console.log('mfa: _localize');
    await super._localize();

    if (this._isInPasswordMode) {
      this._startMfaTokenPasswordMode();
    } else {
      this._startMfaCodeMode();
    }
  };

  async _onLoad() {
    await super._onLoad();
    // called when render of underlying template is complete
    this.toolbarVisible = true;
    this.actionLinkVisible = true;
    this.actionLinkText = Auth0Localizer.getLocalizedText('logout-link-text');
    this.actionLinkTextHandler = this._cancelMfaAndLogout;
    console.log('mfa: _onLoad');
    // this.showSpinner('mfa-please-wait');
    this._pageMode = PAGE_MODE_MFA_CODE;
    try {
      console.log('SEND VERIFICATION');
      const isInitialLoad = true;
      await this._sendVerificationCode(isInitialLoad);
      this._trackMfaEvent(EVENT_MFA_FACTOR_SELECTED);
    } catch (error) {
      if (this.PASSWORD_MODE_ERRORS.includes(error.code)) {
        // expected error... need to collect user password first (always if user did not just use password to sign in)
        this._pageMode = PAGE_MODE_MFA_PASSWORD;
      } else {
        // otherwise show error
        this._showCodedMfaInitError(error.code);
      }
    }

    console.log('SET HTML');
    this.setBody(CodeEntryHtml);
  }

  /**
   * @function
   * @async
   */
  async _startUi() {
    console.log('mfa: _startUi');
    await super._startUi();
    await this._initMfaInputHtml();
    this._showPage();
  }

  render(forceRerender = false) {
    if (this._pageWasLoadedViaRefresh) {
      console.log('mfa: page was loaded via refresh, override refresh; navigate to =>', this._appLoginUrl);
      // special handling for page refresh. This page loads with a signed jwt issued by auth0 actions and
      // it expires in 10 minutes. If the user refreshes the page, they are still in a partially authenticated
      // state and we want to redirect user back through auth0 action to get a new jwt (MFA Grant) and then
      // reload this page with the new jwt. This will do two things: 1) ensure user always has a valid jwt even if
      // they have been at the code page longer than 10 minutes (support issues etc) and 2.) ensure that the user
      // picks up any email address changes immediately based on working with support to change their email address
      window.location = this._appLoginUrl;
      return;
    }
    super.render(forceRerender);
  }
}

export default Auth0MfaCodePageHelper;
